import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiDownload, FiUsers, FiBookOpen, FiTrendingUp, FiCalendar, FiAlertCircle } from 'react-icons/fi';
import apiService from '../../services/api';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
    margin-bottom: 1.5rem;
  }
`;

const Title = styled.h1`
  font-size: clamp(1.5rem, 2.5vw + 1rem, 2rem);
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const ExportButton = styled.button`
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 44px;
  touch-action: manipulation;
  
  &:hover {
    background: var(--primary-hover);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 1rem;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

const FilterSelect = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  min-height: 44px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(motion.div)`
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem;
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const StatTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  margin: 0;
`;

const StatIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.color || 'var(--primary-color)'};
  color: var(--text-inverse);
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const StatChange = styled.div`
  font-size: 0.875rem;
  color: ${props => props.positive ? 'var(--success-color)' : 'var(--error-color)'};
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ReportsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
`;

const ReportCard = styled(motion.div)`
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ReportTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CourseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CourseItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: var(--bg-secondary);
  border-radius: 0;
  border: 1px solid var(--border-color);
`;

const CourseInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const CourseName = styled.span`
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const CourseCode = styled.span`
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const EnrollmentInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
`;

const EnrollmentCount = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const EnrollmentPercentage = styled.span`
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid var(--primary-color);
  border-radius: 0;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
`;

function EnrollmentReports() {
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState({
    totalCourses: 0,
    totalEnrollments: 0,
    averageEnrollment: 0,
    enrollmentGrowth: 0
  });
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchEnrollmentData();
  }, []);

  const fetchEnrollmentData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCourses();
      
      if (response.success) {
        const coursesData = response.courses;
        setCourses(coursesData);
        
        // Calculate statistics
        const totalEnrollments = coursesData.reduce((sum, course) => sum + (course.enrolled_students || 0), 0);
        const averageEnrollment = coursesData.length > 0 ? totalEnrollments / coursesData.length : 0;
        
        setStats({
          totalCourses: coursesData.length,
          totalEnrollments,
          averageEnrollment: Math.round(averageEnrollment * 10) / 10,
          enrollmentGrowth: 12 // Mock data for now
        });
      }
    } catch (error) {
      console.error('Error fetching enrollment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredCourses = () => {
    if (filter === 'all') return courses;
    if (filter === 'popular') return courses.filter(course => (course.enrolled_students || 0) > 10);
    if (filter === 'under-enrolled') return courses.filter(course => (course.enrolled_students || 0) < 5);
    return courses;
  };

  const exportReport = () => {
    const csvContent = generateCSV();
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `enrollment-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const generateCSV = () => {
    const headers = ['Course Code', 'Course Name', 'Instructor', 'Enrolled Students', 'Max Students', 'Enrollment %'];
    const rows = courses.map(course => [
      course.code,
      course.name,
      `${course.instructor_first_name} ${course.instructor_last_name}`,
      course.enrolled_students || 0,
      course.max_students,
      `${Math.round(((course.enrolled_students || 0) / course.max_students) * 100)}%`
    ]);
    
    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const getTopCourses = () => {
    return [...courses]
      .sort((a, b) => (b.enrolled_students || 0) - (a.enrolled_students || 0))
      .slice(0, 5);
  };

  const getUnderEnrolledCourses = () => {
    return courses
      .filter(course => (course.enrolled_students || 0) < (course.max_students * 0.3))
      .sort((a, b) => (a.enrolled_students || 0) - (b.enrolled_students || 0))
      .slice(0, 5);
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <LoadingSpinner />
          <p>Loading enrollment reports...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Enrollment Reports</Title>
        <ExportButton onClick={exportReport}>
          <FiDownload size={16} />
          Export Report
        </ExportButton>
      </Header>

      <Filters>
        <FilterSelect value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">All Courses</option>
          <option value="popular">Popular Courses</option>
          <option value="under-enrolled">Under-Enrolled Courses</option>
        </FilterSelect>
      </Filters>

      <StatsGrid>
        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <StatHeader>
            <StatTitle>Total Courses</StatTitle>
            <StatIcon color="var(--info-color)">
              <FiBookOpen size={20} />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.totalCourses}</StatValue>
          <StatChange positive={true}>
            <FiTrendingUp size={14} />
            Active courses
          </StatChange>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <StatHeader>
            <StatTitle>Total Enrollments</StatTitle>
            <StatIcon color="var(--success-color)">
              <FiUsers size={20} />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.totalEnrollments}</StatValue>
          <StatChange positive={stats.enrollmentGrowth > 0}>
            <FiTrendingUp size={14} />
            +{stats.enrollmentGrowth}% this month
          </StatChange>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <StatHeader>
            <StatTitle>Average Enrollment</StatTitle>
            <StatIcon color="var(--warning-color)">
              <FiTrendingUp size={20} />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.averageEnrollment}</StatValue>
          <StatChange positive={true}>
            <FiTrendingUp size={14} />
            Per course
          </StatChange>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <StatHeader>
            <StatTitle>Enrollment Rate</StatTitle>
            <StatIcon color="var(--accent-color)">
              <FiCalendar size={20} />
            </StatIcon>
          </StatHeader>
          <StatValue>{Math.round((stats.totalEnrollments / (stats.totalCourses * 50)) * 100)}%</StatValue>
          <StatChange positive={true}>
            <FiTrendingUp size={14} />
            Overall capacity
          </StatChange>
        </StatCard>
      </StatsGrid>

      <ReportsGrid>
        <ReportCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <ReportTitle>
            <FiUsers size={18} />
            Top Enrolled Courses
          </ReportTitle>
          <CourseList>
            {getTopCourses().map(course => (
              <CourseItem key={course.id}>
                <CourseInfo>
                  <CourseName>{course.name}</CourseName>
                  <CourseCode>{course.code}</CourseCode>
                </CourseInfo>
                <EnrollmentInfo>
                  <EnrollmentCount>{course.enrolled_students || 0} students</EnrollmentCount>
                  <EnrollmentPercentage>
                    {Math.round(((course.enrolled_students || 0) / course.max_students) * 100)}% capacity
                  </EnrollmentPercentage>
                </EnrollmentInfo>
              </CourseItem>
            ))}
          </CourseList>
        </ReportCard>

        <ReportCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <ReportTitle>
            <FiAlertCircle size={18} />
            Under-Enrolled Courses
          </ReportTitle>
          <CourseList>
            {getUnderEnrolledCourses().map(course => (
              <CourseItem key={course.id}>
                <CourseInfo>
                  <CourseName>{course.name}</CourseName>
                  <CourseCode>{course.code}</CourseCode>
                </CourseInfo>
                <EnrollmentInfo>
                  <EnrollmentCount>{course.enrolled_students || 0} students</EnrollmentCount>
                  <EnrollmentPercentage>
                    {Math.round(((course.enrolled_students || 0) / course.max_students) * 100)}% capacity
                  </EnrollmentPercentage>
                </EnrollmentInfo>
              </CourseItem>
            ))}
          </CourseList>
        </ReportCard>
      </ReportsGrid>

      {getFilteredCourses().length === 0 && (
        <EmptyState>
          <FiUsers size={48} />
          <h3>No courses found</h3>
          <p>Try adjusting your filters or add some courses to see enrollment data.</p>
        </EmptyState>
      )}
    </Container>
  );
}

export default EnrollmentReports;
